import 'moment/locale/es-mx';
import { tracked } from '@glimmer/tracking';
import Service, { service } from '@ember/service';
import moment from 'moment-timezone';

export default class LocaleManagerService extends Service {
  @service analytics;
  @service intl;
  @service currentPractice;
  @service fastboot;
  @service cookies;

  // Default locale, can be overridden by saved locale or browser locale
  @tracked currentLocale = 'en-us';

  availableLocales = [
    { code: 'en-us', name: 'English' },
    { code: 'es-mx', name: 'Español' },
  ];

  COOKIE_KEY = 'clientPortalLocale';

  /**
   * Sets the current locale for the application
   * @param {string} localeCode - The locale code to set (e.g., 'en-us', 'es-mx')
   * @param {string} method - How the locale was selected. Possible values:
   *   - 'user_selected': User explicitly selected the locale via UI
   *   - 'saved_preference': Loaded from saved cookie/localStorage
   *   - 'browser_default': Determined from browser Accept-Language header
   *   - 'url_parameter': Passed via URL parameter (for iframe embedding)
   *   - 'early_init': Set during FastBoot/early initialization to prevent errors
   */
  setLocale = (localeCode, method = 'user_selected') => {
    const previousLocale = this.currentLocale;
    if (this.availableLocales.find(locale => locale.code === localeCode)) {
      this.intl.setLocale([localeCode]);
      this.currentLocale = localeCode;

      // Set moment.js locale for date/calendar formatting
      const momentLocale = localeCode === 'es-mx' ? 'es-mx' : 'en';
      moment.locale(momentLocale);

      this._trackLocaleSelected(localeCode, method);
      this._trackLocaleChanged(previousLocale, localeCode, method);

      // Only persist locale when user explicitly selects it or it's a new browser default
      // Don't persist for 'early_init' or 'saved_preference' to avoid overwriting existing cookies
      if (method === 'user_selected' || method === 'browser_default') {
        this._persistLocale(localeCode);
      }
    }
  };

  _trackLocaleSelected(localeCode, method) {
    // Track language usage and switching - only for Spanish locale events to avoid overwhelming tracking
    if (localeCode === 'es-mx') {
      this.analytics?.selectiveTrack('Client Portal Locale Selected', {
        object: 'Locale',
        action: 'Selected',
        'locale_code': localeCode,
        method,
      });
    }
  }

  _trackLocaleChanged(previousLocale, localeCode, method) {
    // Only track locale changes involving Spanish (either from or to Spanish)
    if (
      previousLocale !== localeCode &&
      method === 'user_selected' &&
      (previousLocale === 'es-mx' || localeCode === 'es-mx')
    ) {
      this.analytics?.selectiveTrack('Client Portal Locale Changed', {
        object: 'Locale',
        action: 'Changed',
        'from_locale': previousLocale,
        'to_locale': localeCode,
        method,
      });
    }
  }

  _persistLocale(localeCode) {
    // Persist locale preference in both cookie (for FastBoot) and localStorage (browser fallback)

    // Set cookie (available in both FastBoot and browser)
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + 30); // 30 days

    // Write the new cookie at root path
    this.cookies.write(this.COOKIE_KEY, localeCode, {
      path: '/',
      expires: expirationDate,
      sameSite: 'lax',
    });
  }

  async initializeLocale() {
    // If internationalization is disabled, default to english
    if (!this.isEnabled) {
      this.intl.setLocale([this.currentLocale]);
      return;
    }

    // Priority order: URL param (for iframe) > saved preference > browser locale > default
    const urlLocale = this._getUrlLocale();
    const savedLocale = this._getSavedLocale();
    const browserLocale = this._getBrowserLocale();
    const defaultLocale = this._determineDefaultLocale(browserLocale);

    // Determine which locale to use and how it was selected
    let localeToUse, method;

    if (urlLocale && this.availableLocales.find(locale => locale.code === urlLocale)) {
      // URL parameter takes highest priority (for iframe embedding)
      localeToUse = urlLocale;
      method = 'url_parameter';
    } else if (savedLocale) {
      localeToUse = savedLocale;
      method = 'saved_preference';
    } else {
      localeToUse = defaultLocale;
      method = 'browser_default';
    }

    this.setLocale(localeToUse, method);
  }

  _getUrlLocale() {
    // Check for locale in URL parameters (for iframe embedding)
    if (this.fastboot?.isFastBoot) {
      // Server-side: parse from request URL
      try {
        // eslint-disable-next-line no-undef
        const URL = FastBoot.require('url').URL;
        // fastboot.request.protocol includes the colon (e.g., "https:")
        const protocol = this.fastboot.request.protocol || 'https:';
        const host = this.fastboot.request.host;
        const requestUrl = this.fastboot.request.url || '/'; // Default to root if URL is undefined

        const url = new URL(requestUrl, `${protocol}//${host}`);
        return url.searchParams.get('locale');
      } catch (error) {
        // If URL parsing fails in Fastboot, return null to fall back to other locale detection
        // eslint-disable-next-line no-console
        console.warn('Failed to parse URL in Fastboot:', error);
        return null;
      }
    } else {
      // Client-side: parse from window.location
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('locale');
    }
  }

  _getSavedLocale() {
    // Try cookie first (works in both FastBoot and browser)
    const cookieValue = this.cookies.read(this.COOKIE_KEY);
    if (cookieValue) {
      return cookieValue;
    }

    return null;
  }

  _getBrowserLocale() {
    // Get browser locale
    if (this.fastboot?.isFastBoot) {
      // Server-side: parse Accept-Language header
      const acceptLanguage = this.fastboot.request.headers['accept-language'];
      if (acceptLanguage) {
        // Parse first language from Accept-Language header (e.g., "en-US,en;q=0.9,es;q=0.8")
        const primaryLanguage = acceptLanguage.split(',')[0].trim().toLowerCase();
        return primaryLanguage;
      }
      return null;
    } else {
      // Client-side: use navigator.language
      return navigator.language.toLowerCase();
    }
  }

  _determineDefaultLocale(browserLocale) {
    return (
      this.availableLocales.find(
        locale => browserLocale && browserLocale.startsWith(locale.code.split('-')[0])
      )?.code || this.currentLocale
    );
  }

  get currentLocaleName() {
    return (
      this.availableLocales.find(locale => locale.code === this.currentLocale)?.name || 'English'
    );
  }

  get currentLocaleForStripe() {
    return this.currentLocale === 'es-mx' ? 'es' : undefined;
  }

  get isEnabled() {
    // Check feature flag for internationalization
    return this.currentPractice.get('featureClientPortalInternationalization');
  }
}
